<template>
  <b-form-group
    :label="label"
    :description="$t('password_form.description')"
  >
    <div class="two-field-input">
      <PasswordField
        :id="passwordId"
        :value="value"
        autocomplete="new-password"
        placeholder="password_form.placeholder"
        :invalid="v$.password.$error"
        @input="emit('input', $event)"
        @blur="v$.password.$touch()"
      />
      <PasswordField
        :id="repeatId"
        :value="repeat"
        autocomplete="new-password"
        placeholder="password_form.repeat"
        :invalid="v$.repeat.$error"
        @input="emit('update:repeat', $event)"
        @blur="v$.repeat.$touch()"
      />
    </div>
    <div v-if="v$.password.$error || v$.repeat.$error" class="d-block invalid-feedback">
      <div v-if="v$.password.required.$invalid" v-text="$t('password_form.error.required')" />
      <div v-if="v$.password.minLength.$invalid" v-text="$t('password_form.error.min_length')" />
      <div v-if="v$.password.complexity.$invalid" v-text="$t('password_form.error.complexity')" />
      <div v-if="v$.password.isTrimmed.$invalid" v-text="$t('password_form.error.trimmed')" />
      <div v-if="v$.repeat.$error" v-text="$t('password_form.error.repeat')" />
    </div>
  </b-form-group>
</template>

<script setup>
/**
 * Input for a new password and its repetition, including validation.
 * The validation results are collected by a parent's useVuelidate() under the key `passwordForm`,
 * so the parent's $validate(), $touch() and $invalid include this form.
 */
import { computed, toRef } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { minLength, required, sameAs } from '@vuelidate/validators'
import i18n from '@/helper/i18n'
import PasswordField from '@/components/Login/PasswordField.vue'

const props = defineProps({
  /** The password, usable with v-model. */
  value: { type: String, required: true },
  /** The repeated password, usable with `:repeat.sync`. */
  repeat: { type: String, required: true },
  label: { type: String, default: () => i18n('password_form.label') },
  passwordId: { type: String, default: undefined },
  repeatId: { type: String, default: undefined },
})

const emit = defineEmits(['input', 'update:repeat'])

const rules = computed(() => ({
  password: {
    required,
    minLength: minLength(8),
    complexity: (value) => /[a-z]/.test(value) && /[A-Z]/.test(value) && /[0-9]/.test(value),
    isTrimmed: (value) => value.length === value.trim().length,
  },
  repeat: { required, sameAs: sameAs(props.value) },
}))

const v$ = useVuelidate(
  rules,
  { password: toRef(props, 'value'), repeat: toRef(props, 'repeat') },
  { $registerAs: 'passwordForm' },
)
</script>

<style lang="scss" scoped>
.two-field-input {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem 1rem;

  > * {
    // rem, so the 14px the global reset gives every div does not shrink the basis
    flex: 1 0 16rem;
    max-width: 100%;
  }

  ::v-deep .input-group {
    margin-bottom: 0 !important;
  }
}
</style>
